import { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import { useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { Helmet } from 'react-helmet-async';
import { Card, CardContent } from '@/components/ui/card';
import { TableOfContents } from '@/components/TableOfContents';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { Skeleton } from '@/components/ui/skeleton';

interface DogBreedImage {
  small: string;
  medium: string;
  large: string;
  alt: string;
}

interface DogBreed {
  id: string;
  name: string;
  image: DogBreedImage;
  description: string;
  characteristics: string;
  health: string;
  suitability: string;
  lifespan: string;
  specialties: string;
}

const PLACEHOLDER_IMAGE = '/placeholder-dog.svg';

const BreedCardSkeleton = () => (
  <div className="w-full">
    <Card className="overflow-hidden">
      <Skeleton className="w-full h-[400px]" />
      <div className="p-8 space-y-6">
        <Skeleton className="h-8 w-2/3" />
        <Skeleton className="h-24 w-full" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Skeleton className="h-40" />
          <Skeleton className="h-40" />
          <Skeleton className="h-40" />
          <Skeleton className="h-40" />
        </div>
        <Skeleton className="h-24" />
        <Skeleton className="h-12 w-48 ml-auto" />
      </div>
    </Card>
  </div>
);

const Hunderassen = () => {
  const { t, language } = useLanguage();
  const location = useLocation();
  const [activeBreed, setActiveBreed] = useState<string | null>(null);
  const [imageErrors, setImageErrors] = useState<Record<string, boolean>>({});
  
  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (hash) {
      setActiveBreed(hash);
        const element = document.getElementById(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
    } else if (dogBreeds.length > 0) {
      setActiveBreed(dogBreeds[0].id);
    }
  }, [location.hash]);

  const handleScroll = useCallback(() => {
    const sections = dogBreeds.flatMap(breed => [
      breed.id,
      `${breed.id}-description`,
      `${breed.id}-characteristics`,
      `${breed.id}-health`,
      `${breed.id}-suitability`,
      `${breed.id}-specialties`
    ]);

    for (const section of sections) {
      const element = document.getElementById(section);
      if (element) {
        const rect = element.getBoundingClientRect();
        if (rect.top <= 100 && rect.bottom >= 100) {
          setActiveBreed(section);
          break;
        }
      }
    }
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);
  
  const dogBreeds: DogBreed[] = [
    {
      id: "labrador-retriever",
      name: "Labrador Retriever",
      image: {
        small: "/lovable-uploads/optimized/Labrador-Retriever-400.webp",
        medium: "/lovable-uploads/optimized/Labrador-Retriever-800.webp",
        large: "/lovable-uploads/optimized/Labrador-Retriever-1200.webp",
        alt: "Labrador Retriever mit freundlichem Gesichtsausdruck"
      },
      description: language === 'de'
        ? "Der Labrador Retriever ist weltweit eine der beliebtesten Hunderassen. Diese freundlichen, intelligenten und anpassungsfähigen Hunde zeichnen sich durch ihr ausgeglichenes Wesen aus. Labradors sind bekannt für ihre Gutmütigkeit, Geduld und ihren Arbeitseifer."
        : "The Labrador Retriever is one of the most popular dog breeds worldwide. These friendly, intelligent, and adaptable dogs are characterized by their balanced nature. Labradors are known for their good nature, patience, and diligence.",
      characteristics: language === 'de'
        ? "Freundlich, intelligent, ausgeglichen, verspielt, geduldig und sehr loyal. Labradors sind äußerst lernwillig und möchten ihren Menschen gefallen."
        : "Friendly, intelligent, balanced, playful, patient, and very loyal. Labradors are extremely eager to learn and want to please their humans.",
      health: language === 'de'
        ? "Hüftdysplasie, Ellbogendysplasie, progressive Retinaatrophie und Adipositas gehören zu den typischen Gesundheitsproblemen. Auch Ohrinfektionen können aufgrund ihrer hängenden Ohren häufiger auftreten."
        : "Hip dysplasia, elbow dysplasia, progressive retinal atrophy, and obesity are among the typical health problems. Ear infections can also occur more frequently due to their hanging ears.",
      suitability: language === 'de'
        ? "Hervorragend für Familien mit Kindern, als Therapie- und Assistenzhund, für aktive Singles und Senioren. Auch als Jagdhund, Rettungshund und Drogenspürhund werden sie häufig eingesetzt."
        : "Excellent for families with children, as therapy and assistance dogs, for active singles and seniors. They are also frequently used as hunting dogs, rescue dogs, and drug detection dogs.",
      lifespan: "10-12 Jahre",
      specialties: language === 'de'
        ? "Labradors haben eine natürliche Affinität zu Wasser dank ihrer wasserabweisenden Doppelschicht des Fells und ihrer \"Otterschweif\"-Rute. Sie benötigen viel Bewegung und geistige Auslastung, um glücklich zu sein."
        : "Labradors have a natural affinity for water thanks to their water-repellent double coat and their \"otter tail\". They need a lot of exercise and mental stimulation to be happy."
    },
    {
      id: "deutscher-schaeferhund",
      name: "Deutscher Schäferhund",
      image: {
        small: "/lovable-uploads/optimized/Deutscher-Schäferhund-400.webp",
        medium: "/lovable-uploads/optimized/Deutscher-Schäferhund-800.webp",
        large: "/lovable-uploads/optimized/Deutscher-Schäferhund-1200.webp",
        alt: "Deutscher Schäferhund mit ausgeprägtem Gesicht"
      },
      description: language === 'de'
        ? "Der Deutsche Schäferhund ist für seine Intelligenz, Treue und Vielseitigkeit bekannt. Als Arbeitshund gezüchtet, zeichnet er sich durch hohe Lernfähigkeit und Bereitschaft zur Zusammenarbeit aus."
        : "The German Shepherd is known for its intelligence, loyalty, and versatility. Bred as a working dog, it is characterized by high learning ability and willingness to cooperate.",
      characteristics: language === 'de'
        ? "Intelligent, loyal, mutig, selbstsicher, arbeitswillig und wachsam. Deutsche Schäferhunde sind sehr eng mit ihrer Familie verbunden und beschützerisch."
        : "Intelligent, loyal, courageous, self-confident, willing to work, and vigilant. German Shepherds are very closely connected to their family and protective.",
      health: language === 'de'
        ? "Hüftdysplasie, Ellbogendysplasie, degenerative Myelopathie und Magendrehung sind häufige Probleme. Die abfallende Rückenlinie mancher Zuchtlinien kann zu Rückenproblemen führen."
        : "Hip dysplasia, elbow dysplasia, degenerative myelopathy, and gastric torsion are common problems. The sloping backline of some breeding lines can lead to back problems.",
      suitability: language === 'de'
        ? "Ideal für aktive Familien, Diensthundführer, als Polizei-, Rettungs- und Wachhund. Bei guter Sozialisierung auch für Familien mit Kindern geeignet."
        : "Ideal for active families, service dog handlers, as police, rescue, and guard dogs. With good socialization, also suitable for families with children.",
      lifespan: "9-13 Jahre",
      specialties: language === 'de'
        ? "Deutsche Schäferhunde benötigen konsequente Erziehung und viel körperliche sowie geistige Beschäftigung. Sie sind sehr menschenbezogen und leiden unter Einsamkeit."
        : "German Shepherds need consistent training and plenty of physical and mental activity. They are very people-oriented and suffer from loneliness."
    },
    {
      id: "golden-retriever",
      name: "Golden Retriever",
      image: {
        small: "/lovable-uploads/optimized/Golden-Retriever-400.webp",
        medium: "/lovable-uploads/optimized/Golden-Retriever-800.webp",
        large: "/lovable-uploads/optimized/Golden-Retriever-1200.webp",
        alt: "Golden Retriever mit sanftmütigem Gesichtsausdruck"
      },
      description: language === 'de'
        ? "Golden Retriever gehören zu den freundlichsten und geduldigsten Hunderassen. Mit ihrem sanften Wesen und ihrer natürlichen Freude an Menschen sind sie perfekte Familienhunde."
        : "Golden Retrievers are among the friendliest and most patient dog breeds. With their gentle nature and natural joy in people, they are perfect family dogs.",
      characteristics: language === 'de'
        ? "Freundlich, sanftmütig, intelligent, vertrauensvoll, geduldig und anhänglich. Golden Retriever sind fast nie aggressiv und haben einen natürlichen Willen zu gefallen."
        : "Friendly, gentle, intelligent, trusting, patient, and affectionate. Golden Retrievers are almost never aggressive and have a natural desire to please.",
      health: language === 'de'
        ? "Hüftdysplasie, Herzprobleme, verschiedene Krebsarten (besonders Lymphome) und Augenerkrankungen können auftreten. Auch Allergien und Hautprobleme sind nicht selten."
        : "Hip dysplasia, heart problems, various types of cancer (especially lymphomas), and eye diseases can occur. Allergies and skin problems are also not uncommon.",
      suitability: language === 'de'
        ? "Hervorragend für Familien mit Kindern jeden Alters, als Therapie- und Assistenzhund, für Senioren und aktive Menschen. Auch als Rettungshund und bei der Jagd werden sie eingesetzt."
        : "Excellent for families with children of all ages, as therapy and assistance dogs, for seniors and active people. They are also used as rescue dogs and in hunting.",
      lifespan: "10-12 Jahre",
      specialties: language === 'de'
        ? "Golden Retriever haaren stark und benötigen regelmäßige Fellpflege. Sie lieben Wasser und bleiben oft auch im Erwachsenenalter verspielt und jung im Geist."
        : "Golden Retrievers shed a lot and need regular grooming. They love water and often remain playful and young at heart even in adulthood."
    },
    {
      id: "franzoesische-bulldogge",
      name: "Französische Bulldogge",
      image: {
        small: "/lovable-uploads/optimized/Französische-Bulldoge-400.webp",
        medium: "/lovable-uploads/optimized/Französische-Bulldoge-800.webp",
        large: "/lovable-uploads/optimized/Französische-Bulldoge-1200.webp",
        alt: "Französische Bulldogge mit ausgeprägtem Gesicht"
      },
      description: language === 'de'
        ? "Die Französische Bulldogge hat sich in den letzten Jahren zu einer der beliebtesten Hunderassen entwickelt. Diese kompakten Begleithunde sind für ihre Fledermausohren und ihr freundliches, anpassungsfähiges Wesen bekannt."
        : "The French Bulldog has become one of the most popular dog breeds in recent years. These compact companion dogs are known for their bat ears and their friendly, adaptable nature.",
      characteristics: language === 'de'
        ? "Freundlich, verspielt, anhänglich, temperamentvoll, anpassungsfähig und gesellig. Französische Bulldoggen sind auch ruhigere Vertreter unter den kleinen Rassen."
        : "Friendly, playful, affectionate, temperamental, adaptable, and sociable. French Bulldogs are also quieter representatives among the small breeds.",
      health: language === 'de'
        ? "Atemwegsprobleme durch brachyzephale (kurzköpfige) Schädelform, Wirbelsäulenprobleme, Augenerkrankungen und Allergien sind häufig. Hitzeverträglichkeit ist gering."
        : "Respiratory problems due to brachycephalic (short-headed) skull shape, spinal problems, eye diseases, and allergies are common. Heat tolerance is low.",
      suitability: language === 'de'
        ? "Gut für Wohnungshaltung, Stadtleben, Familien mit älteren Kindern, Senioren und Singles. Nicht geeignet für intensive sportliche Aktivitäten oder heiße Klimazonen."
        : "Good for apartment living, city life, families with older children, seniors, and singles. Not suitable for intensive sporting activities or hot climates.",
      lifespan: "10-12 Jahre",
      specialties: language === 'de'
        ? "Französische Bulldoggen können nicht schwimmen! Aufgrund ihrer Körperform können sie in extremer Hitze schnell überhitzen. Die meisten Welpen werden per Kaiserschnitt geboren."
        : "French Bulldogs cannot swim! Due to their body shape, they can overheat quickly in extreme heat. Most puppies are born by cesarean section."
    },
    {
      id: "bulldogge",
      name: "Bulldogge",
      image: {
        small: "/lovable-uploads/optimized/Bulldogge-400.webp",
        medium: "/lovable-uploads/optimized/Bulldogge-800.webp",
        large: "/lovable-uploads/optimized/Bulldogge-1200.webp",
        alt: "Englische Bulldogge mit ausgeprägtem Unterbiss"
      },
      description: language === 'de'
        ? "Die Englische Bulldogge ist bekannt für ihr markantes Erscheinungsbild mit dem gedrungenen Körper und dem ausgeprägten Unterbiss. Trotz ihres manchmal grimmigen Aussehens sind sie sanftmütige und loyale Begleiter."
        : "The English Bulldog is known for its distinctive appearance with a stocky body and pronounced underbite. Despite their sometimes grim appearance, they are gentle and loyal companions.",
      characteristics: language === 'de'
        ? "Gelassen, freundlich, mutig, würdevoll, sanft und stur. Bulldoggen sind sehr menschenbezogen und anhänglich."
        : "Calm, friendly, courageous, dignified, gentle, and stubborn. Bulldogs are very people-oriented and affectionate.",
      health: language === 'de'
        ? "Atemwegsprobleme (brachyzephales Syndrom), Haut- und Falteninfektionen, Hüftprobleme, Herz-Kreislauf-Erkrankungen und Überhitzungsgefahr. Ähnlich wie die Französische Bulldogge werden die meisten Welpen per Kaiserschnitt geboren."
        : "Respiratory problems (brachycephalic syndrome), skin and fold infections, hip problems, cardiovascular diseases, and risk of overheating. Similar to the French Bulldog, most puppies are born by cesarean section.",
      suitability: language === 'de'
        ? "Gut für ruhigere Haushalte, Familien mit älteren, rücksichtsvollen Kindern, Wohnungshaltung und Menschen mit gemäßigtem Aktivitätslevel."
        : "Good for quieter households, families with older, considerate children, apartment living, and people with a moderate activity level.",
      lifespan: "8-10 Jahre",
      specialties: language === 'de'
        ? "Bulldoggen benötigen regelmäßige Pflege ihrer Hautfalten, um Infektionen vorzubeugen. Sie sind hitzeempfindlich und sollten bei warmen Temperaturen nur in den kühleren Morgen- und Abendstunden spazieren gehen."
        : "Bulldogs need regular care of their skin folds to prevent infections. They are sensitive to heat and should only be walked in the cooler morning and evening hours in warm temperatures."
    },
    {
      id: "beagle",
      name: "Beagle",
      image: {
        small: "/lovable-uploads/optimized/Beagle-400.webp",
        medium: "/lovable-uploads/optimized/Beagle-800.webp",
        large: "/lovable-uploads/optimized/Beagle-1200.webp",
        alt: "Beagle mit ausgeprägtem Jagdtrieb"
      },
      description: language === 'de'
        ? "Der Beagle ist ein fröhlicher, neugieriger und energiegeladener Jagdhund. Mit ihrer freundlichen Art und mittleren Größe sind sie beliebte Familienhunde geworden."
        : "The Beagle is a cheerful, curious, and energetic hunting dog. With their friendly nature and medium size, they have become popular family dogs.",
      characteristics: language === 'de'
        ? "Fröhlich, neugierig, freundlich, gesellig, stur und geruchsorientiert. Beagles sind echte Nasenspezialisten mit ausgeprägtem Jagdtrieb."
        : "Cheerful, curious, friendly, sociable, stubborn, and scent-oriented. Beagles are true nose specialists with a pronounced hunting instinct.",
      health: language === 'de'
        ? "Übergewicht, Epilepsie, Hypothyreose (Schilddrüsenunterfunktion), Augenkrankheiten und Bandscheibenprobleme können auftreten."
        : "Obesity, epilepsy, hypothyroidism (underactive thyroid), eye diseases, and intervertebral disc problems can occur.",
      suitability: language === 'de'
        ? "Gut für aktive Familien mit Kindern, Hundesportbegeisterte und als Jagdhund. Weniger geeignet für sehr ruhige Haushalte oder lange Alleinsein."
        : "Good for active families with children, dog sports enthusiasts, and as a hunting dog. Less suitable for very quiet households or long periods of being alone.",
      lifespan: "12-15 Jahre",
      specialties: language === 'de'
        ? "Beagles folgen gerne ihrer Nase und können dadurch ungehorsam erscheinen. Sie neigen zum \"Baying\", einem typischen melodischen Gebell/Heulen, wenn sie eine Fährte aufgenommen haben."
        : "Beagles like to follow their nose and can therefore appear disobedient. They tend to \"bay\", a typical melodic bark/howl, when they have picked up a scent."
    },
    {
      id: "pudel",
      name: "Pudel",
      image: {
        small: "/lovable-uploads/optimized/Pudel-400.webp",
        medium: "/lovable-uploads/optimized/Pudel-800.webp",
        large: "/lovable-uploads/optimized/Pudel-1200.webp",
        alt: "Pudel mit ausgeprägtem Fell"
      },
      description: language === 'de'
        ? "Der Pudel kommt in verschiedenen Größen (Toy, Zwerg, Klein und Großpudel) und gilt als eine der intelligentesten Hunderassen. Das lockige, hypoallergene Fell macht ihn bei Allergikern beliebt."
        : "The Poodle comes in different sizes (Toy, Miniature, Medium, and Standard Poodle) and is considered one of the most intelligent dog breeds. The curly, hypoallergenic coat makes it popular with allergy sufferers.",
      characteristics: language === 'de'
        ? "Hochintelligent, lebhaft, verspielt, würdevoll, sensibel und menschenbezogen. Pudel sind sehr lernwillig und vielseitig begabt."
        : "Highly intelligent, lively, playful, dignified, sensitive, and people-oriented. Poodles are very eager to learn and versatile.",
      health: language === 'de'
        ? "Hüftdysplasie (besonders bei größeren Pudeln), Augenerkrankungen, Epilepsie, Cushing-Syndrom und Hautkrankheiten können vorkommen."
        : "Hip dysplasia (especially in larger poodles), eye diseases, epilepsy, Cushing's syndrome, and skin diseases can occur.",
      suitability: language === 'de'
        ? "Durch die verschiedenen Größen für nahezu jeden Haushalt geeignet. Ideal für Allergiker, aktive Familien, Hundesportler und als Therapiehund."
        : "Due to the different sizes, suitable for almost any household. Ideal for allergy sufferers, active families, dog sports enthusiasts, and as a therapy dog.",
      lifespan: "12-15 Jahre für kleine Pudel, 10-12 Jahre für Großpudel",
      specialties: language === 'de'
        ? "Pudel haaren kaum, benötigen aber regelmäßige Fellpflege und Schur. Sie wurden ursprünglich als Wasserjagdhunde gezüchtet und sind ausgezeichnete Schwimmer."
        : "Poodles hardly shed, but need regular grooming and shearing. They were originally bred as water hunting dogs and are excellent swimmers."
    },
    {
      id: "rottweiler",
      name: "Rottweiler",
      image: {
        small: "/lovable-uploads/optimized/Rottweiler-400.webp",
        medium: "/lovable-uploads/optimized/Rottweiler-800.webp",
        large: "/lovable-uploads/optimized/Rottweiler-1200.webp",
        alt: "Rottweiler mit starkem Schutztrieb"
      },
      description: language === 'de'
        ? "Der Rottweiler ist ein kraftvoller, selbstsicherer Arbeitshund mit starkem Schutztrieb. Bei guter Erziehung ist er ein loyaler, ruhiger und ausgeglichener Begleiter."
        : "The Rottweiler is a powerful, self-confident working dog with a strong protective instinct. With good training, he is a loyal, calm, and balanced companion.",
      characteristics: language === 'de'
        ? "Selbstsicher, loyal, mutig, ruhig, territorial und beschützend. Rottweiler sind intelligente Hunde mit natürlichem Wachtrieb."
        : "Self-confident, loyal, courageous, calm, territorial, and protective. Rottweilers are intelligent dogs with a natural guarding instinct.",
      health: language === 'de'
        ? "Hüft- und Ellbogendysplasie, Herzerkrankungen (insbesondere subaortische Stenose), Krebs und Magendrehung sind typische Gesundheitsprobleme."
        : "Hip and elbow dysplasia, heart diseases (especially subaortic stenosis), cancer, and gastric torsion are typical health problems.",
      suitability: language === 'de'
        ? "Für erfahrene Hundehalter, als Wach- und Schutzhund, für aktive Familien mit älteren Kindern. Weniger geeignet für Anfänger oder sehr hektische Umgebungen."
        : "For experienced dog owners, as a guard and protection dog, for active families with older children. Less suitable for beginners or very hectic environments.",
      lifespan: "8-10 Jahre",
      specialties: language === 'de'
        ? "Rottweiler benötigen konsequente, liebevolle Erziehung und frühe Sozialisierung. In vielen Ländern und Regionen gelten besondere Haltungsvorschriften für diese Rasse."
        : "Rottweilers need consistent, loving training and early socialization. In many countries and regions, special keeping regulations apply to this breed."
    },
    {
      id: "yorkshire-terrier",
      name: "Yorkshire Terrier",
      image: {
        small: "/lovable-uploads/optimized/Yorkshire-Terrier-400.webp",
        medium: "/lovable-uploads/optimized/Yorkshire-Terrier-800.webp",
        large: "/lovable-uploads/optimized/Yorkshire-Terrier-1200.webp",
        alt: "Yorkshire Terrier mit großem Charakter"
      },
      description: language === 'de'
        ? "Der Yorkshire Terrier ist ein kleiner Hund mit großem Charakter. Trotz seiner geringen Größe hat er ein mutiges, selbstbewusstes Temperament, das an seine Vergangenheit als Rattenfänger erinnert."
        : "The Yorkshire Terrier is a small dog with a big character. Despite its small size, it has a courageous, self-confident temperament that is reminiscent of its past as a rat catcher.",
      characteristics: language === 'de'
        ? "Selbstbewusst, anhänglich, mutig, neugierig, wachsam und manchmal dickköpfig. Yorkshire Terrier können trotz ihrer Größe territorial sein."
        : "Self-confident, affectionate, courageous, curious, vigilant, and sometimes stubborn. Yorkshire Terriers can be territorial despite their size.",
      health: language === 'de'
        ? "Patellaluxation (Kniescheibenverrenkung), Legg-Calvé-Perthes-Krankheit, Trachealkoallaps, Zahnprobleme und Lebererkrankungen sind häufig."
        : "Patellar luxation (dislocation of the kneecap), Legg-Calvé-Perthes disease, tracheal collapse, dental problems, and liver diseases are common.",
      suitability: language === 'de'
        ? "Gut für Wohnungshaltung, ältere Menschen, Singles und Familien mit rücksichtsvollen Kindern. Weniger geeignet für Haushalte mit Kleinkindern oder sehr aktive, sportliche Hundehalter."
        : "Good for apartment living, older people, singles, and families with considerate children. Less suitable for households with small children or very active, sporty dog owners.",
      lifespan: "13-16 Jahre",
      specialties: language === 'de'
        ? "Das lange, seidige Fell benötigt tägliche Pflege, wenn es lang getragen wird. Viele Besitzer entscheiden sich für einen praktischen Kurzhaarschnitt. Trotz seiner geringen Größe braucht der Yorkshire Terrier mentale Stimulation und regelmäßige Bewegung."
        : "The long, silky coat needs daily care if worn long. Many owners opt for a practical short haircut. Despite its small size, the Yorkshire Terrier needs mental stimulation and regular exercise."
    },
    {
      id: "deutscher-boxer",
      name: "Deutscher Boxer",
      image: {
        small: "/lovable-uploads/optimized/Deutsche-Boxer-400.webp",
        medium: "/lovable-uploads/optimized/Deutsche-Boxer-800.webp",
        large: "/lovable-uploads/optimized/Deutsche-Boxer-1200.webp",
        alt: "Deutscher Boxer mit ausgeprägtem Gesicht"
      },
      description: language === 'de'
        ? "Der Deutsche Boxer ist ein muskulöser, mittelgroßer Hund mit ausdrucksstarkem Gesicht und verspieltem Charakter. Er bleibt oft bis ins hohe Alter jugendlich und verspielt."
        : "The German Boxer is a muscular, medium-sized dog with an expressive face and playful character. He often remains youthful and playful well into old age.",
      characteristics: language === 'de'
        ? "Verspielt, loyal, beschützend, energiegeladen, intelligent und freundlich. Boxer sind bekannt für ihre Liebe zu Kindern und ihre Geduld."
        : "Playful, loyal, protective, energetic, intelligent, and friendly. Boxers are known for their love of children and their patience.",
      health: language === 'de'
        ? "Herzprobleme (besonders Aortenstenose), Krebs, Hüftdysplasie, Magen-Darm-Probleme und bei kurzköpfigen Linien auch Atemwegsprobleme. Weiße Boxer haben ein erhöhtes Risiko für Taubheit."
        : "Heart problems (especially aortic stenosis), cancer, hip dysplasia, gastrointestinal problems, and respiratory problems in short-headed lines. White boxers have an increased risk of deafness.",
      suitability: language === 'de'
        ? "Hervorragend für aktive Familien mit Kindern, Sportler und Menschen mit Haus und Garten. Weniger geeignet für sehr ruhige Haushalte oder heiße Klimazonen."
        : "Excellent for active families with children, athletes, and people with a house and garden. Less suitable for very quiet households or hot climates.",
      lifespan: "10-12 Jahre",
      specialties: language === 'de'
        ? "Boxer sind hitzeempfindlich aufgrund ihrer etwas verkürzten Schnauze. Sie speicheln oft, besonders nach dem Trinken oder bei Aufregung. Boxer gelten als \"ewige Welpen\", die auch im Erwachsenenalter verspielt und albern sein können."
        : "Boxers are sensitive to heat due to their somewhat shortened snout. They often drool, especially after drinking or when excited. Boxers are considered \"eternal puppies\" who can be playful and silly even in adulthood."
    },
    {
      id: "dackel",
      name: "Dackel (Dachshund)",
      image: {
        small: "/lovable-uploads/optimized/Dackel-400.webp",
        medium: "/lovable-uploads/optimized/Dackel-800.webp",
        large: "/lovable-uploads/optimized/Dackel-1200.webp",
        alt: "Dackel mit langem Körper und kurzen Beinen"
      },
      description: language === 'de'
        ? "Der Dackel, auch Dachshund oder \"Teckel\" genannt, ist mit seinem langen Körper und kurzen Beinen unverwechselbar. Er wurde ursprünglich für die Jagd auf Dachse und anderes Niederwild gezüchtet."
        : "The Dachshund, also called Dachshund or \"Teckel\", is unmistakable with its long body and short legs. It was originally bred to hunt badgers and other small game.",
      characteristics: language === 'de'
        ? "Mutig, eigensinnig, intelligent, neugierig, selbstbewusst und manchmal stur. Dackel haben einen starken Charakter und Jagdinstinkt."
        : "Courageous, headstrong, intelligent, curious, self-confident, and sometimes stubborn. Dachshunds have a strong character and hunting instinct.",
      health: language === 'de'
        ? "Bandscheibenvorfall (IVDD) ist das häufigste Problem aufgrund des langen Rückens. Auch Adipositas, Augenerkrankungen und Patellaluxation können auftreten."
        : "Intervertebral disc disease (IVDD) is the most common problem due to the long back. Obesity, eye diseases, and patellar luxation can also occur.",
      suitability: language === 'de'
        ? "Für Familien mit älteren Kindern, aktive Senioren, Wohnungshaltung und als Jagdbegleiter. Weniger geeignet für Haushalte mit vielen Treppen oder sehr kleinen Kindern."
        : "For families with older children, active seniors, apartment living, and as a hunting companion. Less suitable for households with many stairs or very small children.",
      lifespan: "12-16 Jahre",
      specialties: language === 'de'
        ? "Dackel gibt es in drei Größen (Standard, Zwerg und Kaninchendackel) und drei Fellvarianten (Kurzhaar, Langhaar und Rauhaar). Sie sollten nicht springen oder Treppen steigen müssen, um ihren Rücken zu schonen."
        : "Dachshunds come in three sizes (Standard, Miniature, and Rabbit Dachshund) and three coat types (Smooth, Longhaired, and Wirehaired). They should not have to jump or climb stairs to protect their backs."
    },
    {
      id: "australian-shepherd",
      name: "Australian Shepherd",
      image: {
        small: "/lovable-uploads/optimized/Australian-Shepherd-400.webp",
        medium: "/lovable-uploads/optimized/Australian-Shepherd-800.webp",
        large: "/lovable-uploads/optimized/Australian-Shepherd-1200.webp",
        alt: "Australian Shepherd mit beeindruckender Ausdauer"
      },
      description: language === 'de' 
        ? "Der Australian Shepherd ist ein intelligenter, arbeitsfreudiger Hütehund mit beeindruckender Ausdauer und Bewegungsfreude. \"Aussies\" sind bekannt für ihre oft heterochromen Augen (unterschiedliche Augenfarben)."
        : "The Australian Shepherd is an intelligent, hardworking herding dog with impressive endurance and joy of movement. \"Aussies\" are known for their often heterochromic eyes (different colored eyes).",
      characteristics: language === 'de'
        ? "Intelligent, arbeitswillig, energiegeladen, loyal, wachsam und manchmal reserviert gegenüber Fremden. Aussies haben einen starken Hütetrieb."
        : "Intelligent, willing to work, energetic, loyal, vigilant, and sometimes reserved towards strangers. Aussies have a strong herding instinct.",
      health: language === 'de'
        ? "MDR1-Gendefekt (Medikamentenunverträglichkeit), Hüftdysplasie, Augenerkrankungen (besonders Collie Eye Anomaly), Epilepsie und Autoimmunerkrankungen können auftreten."
        : "MDR1 gene defect (drug intolerance), hip dysplasia, eye diseases (especially Collie Eye Anomaly), epilepsy, and autoimmune diseases can occur.",
      suitability: language === 'de'
        ? "Ideal für aktive Familien, Hundesportler, Landwirte und Menschen mit viel Zeit für Beschäftigung. Nicht geeignet für Anfänger, Menschen mit wenig Zeit oder sehr ruhige Haushalte."
        : "Ideal for active families, dog sports enthusiasts, farmers, and people with plenty of time for engagement. Not suitable for beginners, people with limited time, or very quiet households.",
      lifespan: "12-15 Jahre",
      specialties: language === 'de'
        ? "Australian Shepherds benötigen täglich intensive körperliche und geistige Auslastung. Ohne ausreichende Beschäftigung können sie problematische Verhaltensweisen entwickeln."
        : "Australian Shepherds need daily intensive physical and mental exercise. Without sufficient activity, they can develop problematic behaviors."
    },
    {
      id: "sibirischer-husky",
      name: "Sibirischer Husky",
      image: {
        small: "/lovable-uploads/optimized/Sibirischer-Husky-400.webp",
        medium: "/lovable-uploads/optimized/Sibirischer-Husky-800.webp",
        large: "/lovable-uploads/optimized/Sibirischer-Husky-1200.webp",
        alt: "Sibirischer Husky mit ausgeprägtem Aussehen"
      },
      description: language === 'de'
        ? "Der Sibirischer Husky ist ein athletischer, ausdauernder Schlittenhund mit wolfsähnlichem Aussehen und unabhängigem Charakter. Seine Schönheit macht ihn sehr beliebt, aber sein Charakter stellt viele Halter vor Herausforderungen."
        : "The Siberian Husky is an athletic, enduring sled dog with a wolf-like appearance and independent character. Its beauty makes it very popular, but its character poses challenges for many owners.",
      characteristics: language === 'de'
        ? "Freundlich, unabhängig, intelligent, ausdauernd, stur und mit starkem Jagdtrieb. Huskys sind keine \"Ein-Personen-Hunde\" und mögen in der Regel alle Menschen."
        : "Friendly, independent, intelligent, enduring, stubborn, and with a strong hunting instinct. Huskies are not \"one-person dogs\" and generally like all people.",
      health: language === 'de'
        ? "Augenkrankheiten (besonders Katarakt und progressive Retinaatrophie), Hüftdysplasie, Hypothyreose und Hautprobleme können vorkommen."
        : "Eye diseases (especially cataracts and progressive retinal atrophy), hip dysplasia, hypothyroidism, and skin problems can occur.",
      suitability: language === 'de'
        ? "Für sehr aktive Menschen, Ausdauersportler, Hundeschlittenfahrer und erfahrene Hundehalter. Nicht geeignet für Anfänger, Wohnungshaltung ohne extremen Auslauf oder Menschen mit wenig Zeit."
        : "For very active people, endurance athletes, dog sledders, and experienced dog owners. Not suitable for beginners, apartment living without extreme exercise, or people with limited time.",
      lifespan: "12-14 Jahre",
      specialties: language === 'de'
        ? "Huskys haaren zweimal jährlich extrem stark (\"Fellwechsel\"). Sie haben einen starken Jagdtrieb und sollten daher nicht ohne Leine laufen, wenn nicht absolut sicher abrufbar. In warmen Klimazonen leiden sie unter der Hitze. Sie sind bekannt für ihre \"Gespräche\" und vielfältigen Lautäußerungen."
        : "Huskies shed extremely heavily twice a year (\"shedding season\"). They have a strong hunting instinct and should therefore not run without a leash unless absolutely reliably recallable. In warm climates, they suffer from the heat. They are known for their \"conversations\" and diverse vocalizations."
    },
    {
      id: "chihuahua",
      name: "Chihuahua",
      image: {
        small: "/lovable-uploads/optimized/Chihuahua-400.webp",
        medium: "/lovable-uploads/optimized/Chihuahua-800.webp",
        large: "/lovable-uploads/optimized/Chihuahua-1200.webp",
        alt: "Chihuahua mit großem, selbstbewussten Charakter"
      },
      description: language === 'de'
        ? "Der Chihuahua ist die kleinste anerkannte Hunderasse der Welt. Trotz seiner winzigen Größe hat er einen großen, selbstbewussten Charakter und ist oft mutig bis zur Furchtlosigkeit."
        : "The Chihuahua is the smallest recognized dog breed in the world. Despite its tiny size, it has a large, self-confident character and is often brave to the point of fearlessness.",
      characteristics: language === 'de'
        ? "Selbstbewusst, anhänglich, wachsam, mutig, loyal und manchmal rechthaberisch. Chihuahuas binden sich oft stark an eine Bezugsperson."
        : "Self-confident, affectionate, vigilant, courageous, loyal, and sometimes opinionated. Chihuahuas often bond strongly with a caregiver.",
      health: language === 'de'
        ? "Patellaluxation, Herzprobleme, Hydrocephalus (Wasserkopf), Zahnprobleme durch Überbiss und Hypoglykämie (besonders bei sehr kleinen Exemplaren) sind häufig."
        : "Patellar luxation, heart problems, hydrocephalus (water on the brain), dental problems due to overbite, and hypoglycemia (especially in very small specimens) are common.",
      suitability: language === 'de'
        ? "Gut für Wohnungshaltung, ruhigere Haushalte, Senioren und Singles. Weniger geeignet für Familien mit kleinen Kindern oder sehr aktive, sportliche Hundehalter."
        : "Good for apartment living, quieter households, seniors, and singles. Less suitable for families with small children or very active, sporty dog owners.",
      lifespan: "14-16 Jahre, manchmal sogar bis zu 20 Jahre",
      specialties: language === 'de'
        ? "Chihuahuas haben eine Fontanelle (weiche Stelle am Schädel), die bei manchen Hunden zeitlebens offen bleibt und besonders geschützt werden muss. Sie frieren leicht und benötigen in der kalten Jahreszeit Hundebekleidung."
        : "Chihuahuas have a fontanelle (soft spot on the skull) that remains open for life in some dogs and must be specially protected. They freeze easily and need dog clothing in the cold season."
    },
    {
      id: "shih-tzu",
      name: "Shih Tzu",
      image: {
        small: "/lovable-uploads/optimized/Shih-Tzu-400.webp",
        medium: "/lovable-uploads/optimized/Shih-Tzu-800.webp",
        large: "/lovable-uploads/optimized/Shih-Tzu-1200.webp",
        alt: "Shih Tzu mit luxuriösem Fell"
      },
      description: language === 'de'
        ? "Der Shih Tzu ist ein kleiner Begleithund mit luxuriösem Fell und freundlichem, ausgeglichenem Wesen. Ursprünglich für den chinesischen Kaiserpalast gezüchtet, sind sie heute beliebte Familienhunde."
        : "The Shih Tzu is a small companion dog with luxurious fur and a friendly, balanced nature. Originally bred for the Chinese Imperial Palace, they are now popular family dogs.",
      characteristics: language === 'de'
        ? "Freundlich, liebevoll, verspielt, selbstbewusst, anhänglich und geduldig. Shih Tzus wurden speziell als Gesellschaftshunde gezüchtet und erfüllen diese Rolle perfekt."
        : "Friendly, loving, playful, self-confident, affectionate, and patient. Shih Tzus were specifically bred as companion dogs and fulfill this role perfectly.",
      health: language === 'de'
        ? "Atemwegsprobleme durch die kurze Schnauze, Augenprobleme, Nierenerkrankungen und Gelenkprobleme können auftreten. Besonders bei Hitze muss auf Atemprobleme geachtet werden."
        : "Respiratory problems due to the short snout, eye problems, kidney diseases, and joint problems can occur. Special attention must be paid to breathing problems, especially in hot weather.",
      suitability: language === 'de'
        ? "Ideal für Familien mit Kindern, Senioren, Singles und Wohnungshaltung. Auch als Therapiehund geeignet durch ihr sanftes Wesen."
        : "Ideal for families with children, seniors, singles, and apartment living. Also suitable as a therapy dog due to their gentle nature.",
      lifespan: "10-16 Jahre",
      specialties: language === 'de'
        ? "Shih Tzus benötigen regelmäßige Fellpflege aufgrund ihres langen, seidigen Fells. Sie sind nicht sehr sportlich und bevorzugen gemütliche Spaziergänge und Spielzeiten im Haus."
        : "Shih Tzus need regular grooming due to their long, silky coat. They are not very athletic and prefer comfortable walks and play times in the house."
    },
    {
      id: "dobermann",
      name: "Dobermann",
      image: {
        small: "/lovable-uploads/optimized/Dobermann-400.webp",
        medium: "/lovable-uploads/optimized/Dobermann-800.webp",
        large: "/lovable-uploads/optimized/Dobermann-1200.webp",
        alt: "Dobermann mit ausgeprägtem Gesicht"
      },
      description: language === 'de'
        ? "Der Dobermann ist ein eleganter, muskulöser und intelligenter Arbeitshund. Ursprünglich als Schutzhund gezüchtet, ist er heute ein vielseitiger Begleiter mit ausgeprägter Loyalität."
        : "The Dobermann is an elegant, muscular, and intelligent working dog. Originally bred as a protection dog, it is now a versatile companion with pronounced loyalty.",
      characteristics: language === 'de'
        ? "Intelligent, loyal, wachsam, mutig, energiegeladen und beschützend. Moderne Dobermänner sind bei guter Zucht deutlich weniger aggressiv als ihr Ruf vermuten lässt."
        : "Intelligent, loyal, vigilant, courageous, energetic, and protective. Modern Dobermanns from good breeding are significantly less aggressive than their reputation suggests.",
      health: language === 'de'
        ? "Neigung zu Herzerkrankungen (DCM), von Willebrand-Krankheit (Blutgerinnungsstörung), Wobbler-Syndrom und Hüftdysplasie. Regelmäßige tierärztliche Kontrollen sind wichtig."
        : "Prone to heart disease (DCM), von Willebrand's disease (blood clotting disorder), Wobbler syndrome, and hip dysplasia. Regular veterinary check-ups are important.",
      suitability: language === 'de'
        ? "Geeignet für aktive Menschen mit Hundeerfahrung, die Zeit für Training und Beschäftigung haben. Nicht für Anfänger oder sehr passive Halter."
        : "Suitable for active people with dog experience who have time for training and activities. Not for beginners or very passive owners.",
      lifespan: "10-13 Jahre",
      specialties: language === 'de'
        ? "Exzellenter Schutz- und Begleithund mit hoher Lernfähigkeit. Braucht konsequente, aber liebevolle Erziehung und viel geistige und körperliche Auslastung."
        : "Excellent protection and companion dog with high learning ability. Needs consistent but loving education and lots of mental and physical exercise."
    }
  ];

  const handleImageError = (breedId: string) => {
    setImageErrors(prev => ({ ...prev, [breedId]: true }));
  };

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      <Helmet>
        <title>{language === 'de' ? 'Hunderassen - Hundeschulenfinder' : 'Dog Breeds - Dog School Finder'}</title>
        <meta 
          name="description" 
          content={language === 'de' 
            ? 'Entdecken Sie verschiedene Hunderassen und ihre Eigenschaften' 
            : 'Discover different dog breeds and their characteristics'
          } 
        />
        <link rel="preload" as="image" href="/lovable-uploads/optimized/Labrador-Retriever-400.webp" />
      </Helmet>
      <Header />
      <main className="flex-grow container mx-auto px-4 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          <aside className="lg:w-1/4 shrink-0">
            <div className="sticky top-4">
              <TableOfContents
                items={dogBreeds.map(breed => ({
                  id: breed.id,
                  title: breed.name
                }))}
                defaultOpen={true}
              />
            </div>
          </aside>
          <div className="lg:w-3/4">
            <h1 className="text-4xl font-bold mb-8 text-center text-gray-800">
              {language === 'de' ? 'Hunderassen' : 'Dog Breeds'}
            </h1>
            <div className="flex flex-col space-y-8">
            {dogBreeds.map((breed) => (
                <motion.div
                key={breed.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  className="w-full"
                id={breed.id}
                >
                  <Card className="overflow-hidden hover:shadow-lg transition-shadow duration-300">
                    <div className="relative w-full h-[400px]">
                      <img
                        src={imageErrors[breed.id] ? PLACEHOLDER_IMAGE : breed.image.small}
                        srcSet={`
                          ${breed.image.small} 400w,
                          ${breed.image.medium} 800w,
                          ${breed.image.large} 1200w
                        `}
                        sizes="(max-width: 600px) 400px,
                               (max-width: 1200px) 800px,
                               1200px"
                        loading="lazy"
                        alt={breed.image.alt}
                        className="w-full h-full object-cover"
                        onError={() => handleImageError(breed.id)}
                      />
                    </div>
                    <CardContent className="p-8">
                      <div className="space-y-6">
                        <h2 className="text-3xl font-semibold text-gray-800">
                          {breed.name}
                        </h2>
                        <div id={`${breed.id}-description`}>
                          <h3 className="text-xl font-semibold mb-2 text-gray-700">
                            {language === 'de' ? 'Beschreibung' : 'Description'}
                          </h3>
                          <p className="text-lg text-gray-600">
                            {breed.description}
                          </p>
                </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div id={`${breed.id}-characteristics`} className="bg-blue-50 rounded-lg p-6 shadow-sm">
                            <h3 className="text-xl font-semibold mb-2 text-blue-800">
                              {language === 'de' ? 'Eigenschaften' : 'Characteristics'}
                            </h3>
                        <p className="text-gray-700">{breed.characteristics}</p>
                          </div>
                          <div id={`${breed.id}-health`} className="bg-red-50 rounded-lg p-6 shadow-sm">
                            <h3 className="text-xl font-semibold mb-2 text-red-800">
                              {language === 'de' ? 'Gesundheit' : 'Health'}
                            </h3>
                        <p className="text-gray-700">{breed.health}</p>
                          </div>
                          <div id={`${breed.id}-suitability`} className="bg-green-50 rounded-lg p-6 shadow-sm">
                            <h3 className="text-xl font-semibold mb-2 text-green-800">
                              {language === 'de' ? 'Eignung' : 'Suitability'}
                            </h3>
                        <p className="text-gray-700">{breed.suitability}</p>
                          </div>
                          <div id={`${breed.id}-specialties`} className="bg-purple-50 rounded-lg p-6 shadow-sm">
                            <h3 className="text-xl font-semibold mb-2 text-purple-800">
                              {language === 'de' ? 'Besonderheiten' : 'Specialties'}
                            </h3>
                        <p className="text-gray-700">{breed.specialties}</p>
                          </div>
                  </div>
                        <div className="bg-gray-50 rounded-lg p-6 shadow-sm text-center">
                          <h3 className="text-xl font-semibold mb-2 text-gray-800">
                            {language === 'de' ? 'Durchschnittliche Lebenserwartung' : 'Average Life Expectancy'}
                          </h3>
                          <p className="text-2xl font-bold text-gray-900">{breed.lifespan}</p>
                    </div>
                        <div className="flex justify-end items-center pt-4 border-t border-gray-200">
                          <Link
                            to="/"
                            className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 text-lg"
                          >
                            {language === 'de' ? 'Hundeschule in deiner Nähe' : 'Dog School Near You'}
                            <svg
                              className="w-5 h-5 ml-2"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 5l7 7-7 7"
                              />
                            </svg>
                          </Link>
                  </div>
                </div>
                    </CardContent>
                  </Card>
                </motion.div>
            ))}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Hunderassen;
